import { useCallback, useMemo } from "react";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { classNames } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { ArticleType } from "@shared/url";
import { SaleAccommodationFeatures } from "@shared/graphql";
import { article, common } from "../../i18n/sharable-defs";
import { SkeletonableProps } from "../../tools/components";
import { getEnumKeyByValue } from "../../tools/enum";
import SpriteIcon from "../medias/sprite-icon";
import "./features.scss";

const transDefs = defineMessages({
  title: { id: "article.rubrics.features-title", defaultMessage: "Caractéristiques principales" },
  yesNo: { id: "article.rubrics.features-yes-no", defaultMessage: "{yes, select, true {oui} other {non}}" },
  allow: { id: "article.rubrics.features-allow", defaultMessage: "{yes, select, true {autorisé} other {non-autorisé}}" },
  reference: { id: "article.rubrics.feature-ref", defaultMessage: "Référence" },
  construction: { id: "article.rubrics.feature-construction", defaultMessage: "Année de construction" },
});

const salesDetailAttributes: Array<keyof SaleAccommodationFeatures> = [
  "isNew",
  "heatings",
  "isFurnished",
  "parkings",
  "canBeSecondaryHome",
  "isSellableToForeigners"
];

type Props = SkeletonableProps<any> & {
  type: ArticleType;
}

const FeaturesPart = ({ className, loading, type, data }: Props) => {

  const intl = useIntl();
  const { transLib } = useLocaleFormatter();

  const withMainFeatures = useMemo<boolean>(() => {
    const valid = Object.entries(data || [])
                        .filter(([ key, _]) => !salesDetailAttributes.includes(key as any))
                        .filter(([ _, value ]) => typeof value === "boolean")
                        .filter(([ key, value ]) => value || (key === "isSmokerCompliant" && !value));
    return valid.length > 0;
  }, [ data ]);

  const mergedSaunaFeatures = useMemo(() => ({
    ...(!!data && "withSauna" in data && !!data.withSauna) && { withSauna: article.sauna },
    ...(!!data && "withHotTub" in data && !!data.withHotTub) && { withHotTub: article.hotTub },
    ...(!!data && "withHammam" in data && !!data.withHammam) && { withHammam: article.hammam }
  }), [ type, data ]);

  const textualizeSaunaLabel = useCallback((items: MessageDescriptor[]) => {
    return items.reduce((acc, cur, index) => {
      const separator = (items.length > 1 && index + 1 === items.length) ? ` ${intl.formatMessage(common.and)}` : ",";
      const value = intl.formatMessage(cur);
      return index === 0 ? value : `${acc}${separator} ${value}`;
    }, "");
  }, [ intl ]);

  const saleClasses = classNames("sale-details-part", className);
  const wrapperClasses = classNames("features-part", className);
  const listClasses = classNames("features-list", getEnumKeyByValue(ArticleType, type)?.toLowerCase());

  if (loading)
    return null;

  return (
    <>
      {type === ArticleType.SALE &&
        <div className={saleClasses}>
          <ul className={listClasses}>
            {!!data.referenceId &&
              <li>
                <span className="label">{`${intl.formatMessage(transDefs.reference)} :`}</span>
                <span>{data.referenceId}</span>
              </li>
            }
            {!!data.objectType &&
              <li>
                <span className="label">{`${intl.formatMessage(article.type)} :`}</span>
                <span>{transLib.match("objectTypes", data.objectType)}</span>
              </li>
            }
            {!!data.floor &&
              <li>
                <span className="label">{`${intl.formatMessage(article.floor)} :`}</span>
                <span>{data.floor.value}</span>
              </li>
            }
            {!!data.constructionYear &&
              <li>
                <span className="label">{`${intl.formatMessage(transDefs.construction)} :`}</span>
                <span>{data.constructionYear}</span>
              </li>
            }
            {!!data.isNew &&
              <li>
                <span className="label">{`${intl.formatMessage(article.new)} :`}</span>
                <span>{intl.formatMessage(transDefs.yesNo, { yes: !!data.isNew })}</span>
              </li>
            }
            {!!data.heatings &&
              <li>
                <span className="label">{`${intl.formatMessage(article.heating)} :`}</span>
                <span>{data.heatings.map((h: any) => transLib.match("heatingSystems", h).join(", "))}</span>
              </li>
            }
            {!!data.isFurnished &&
              <li>
                <span className="label">{`${intl.formatMessage(article.furnished)} :`}</span>
                <span>{intl.formatMessage(transDefs.yesNo, { yes: data.isFurnished })}</span>
              </li>
            }
            {!!data.parkings &&
              <li>
                <span className="label">{`${intl.formatMessage(article.parking)} :`}</span>
                <span>{data.parkings}</span>
              </li>
            }
            {(typeof data.canBeSecondaryHome !== "undefined") &&
              <li>
                <span className="label">{`${intl.formatMessage(article.secondaryHome)} :`}</span>
                <span>{intl.formatMessage(transDefs.allow, { yes: data.canBeSecondaryHome })}</span>
              </li>
            }
            {(typeof data.isSellableToForeigners !== "undefined") &&
              <li>
                <span className="label">{`${intl.formatMessage(article.forForeigners)} :`}</span>
                <span>{intl.formatMessage(transDefs.allow, { yes: data.isSellableToForeigners })}</span>
              </li>
            }
          </ul>
        </div>
      }
      {withMainFeatures &&
        <div className={wrapperClasses}>
          <h2>{intl.formatMessage(transDefs.title)}</h2>
          <ul className={listClasses}>

            {!!data.withParking &&
              <li className={classNames("throughable", { "no": !data.withParking })}>
                <span className="label"><SpriteIcon hash="features.parking" /></span>
                <span>{intl.formatMessage(article.parking)}</span>
              </li>
            }
            {!!data.withLift &&
              <li className={classNames("throughable", { "no": !data.withLift })}>
                <span className="label"><SpriteIcon hash="features.lift" /></span>
                <span>{intl.formatMessage(article.lift)}</span>
              </li>
            }
            {!!data.withBalcony &&
              <li className={classNames("throughable", { "no": !data.withBalcony })}>
                <span className="label"><SpriteIcon hash="features.balcony" /></span>
                <span>{intl.formatMessage(article.balcony)}</span>
              </li>
            }
            {!!data.withTerrace &&
              <li className={classNames("throughable", { "no": !data.withTerrace })}>
                <span className="label"><SpriteIcon hash="features.terrace" /></span>
                <span>{intl.formatMessage(article.terrace)}</span>
              </li>
            }
            {!!data.withFireplace &&
              <li className={classNames("throughable", { "no": !data.withFireplace })}>
                <span className="label"><SpriteIcon hash="features.fireplace" /></span>
                <span>{intl.formatMessage(article.fireplace)}</span>
              </li>
            }
            {!!data.withPool &&
              <li className={classNames("throughable", { "no": !data.withPool })}>
                <span className="label"><SpriteIcon hash="features.pool" /></span>
                <span>{intl.formatMessage(article.pool)}</span>
              </li>
            }
            {Object.keys(mergedSaunaFeatures).length > 0 &&
              <li className={classNames("throughable", { "no": false })}>
                <span className="label"><SpriteIcon hash="features.sauna" /></span>
                <span>{textualizeSaunaLabel(Object.values(mergedSaunaFeatures))}</span>
              </li>
            }
            {!!data.withWIFI &&
              <li className={classNames("throughable", { "no": !data.withWIFI })}>
                <span className="label"><SpriteIcon hash="features.wifi" /></span>
                <span>{intl.formatMessage(article.wifi)}</span>
              </li>
            }
            {!!data.withTV &&
              <li className={classNames("throughable", { "no": !data.withTV })}>
                <span className="label"><SpriteIcon hash="features.tv" /></span>
                <span>{intl.formatMessage(article.tv)}</span>
              </li>
            }
            {!!data.withLaundryRoom &&
              <li className={classNames("throughable", { "no": !data.withLaundryRoom })}>
                <span className="label"><SpriteIcon hash="features.laundry-room" /></span>
                <span>{intl.formatMessage(article.laundryRoom)}</span>
              </li>
            }
            {!!data.withWashingMachine &&
              <li className={classNames("throughable", { "no": !data.withWashingMachine })}>
                <span className="label"><SpriteIcon hash="features.washing-machine" /></span>
                <span>{intl.formatMessage(article.washingMachine)}</span>
              </li>
            }
            {!!data.withDishwasher &&
              <li className={classNames("throughable", { "no": !data.withDishwasher })}>
                <span className="label"><SpriteIcon hash="features.dishwasher" /></span>
                <span>{intl.formatMessage(article.dishwasher)}</span>
              </li>
            }

            {type === ArticleType.RENTAL &&
              <>
                {!!data.withOven &&
                  <li className={classNames("throughable", { "no": !data.withOven })}>
                    <span className="label"><SpriteIcon hash="features.oven" /></span>
                    <span>{intl.formatMessage(article.oven)}</span>
                  </li>
                }
                {!!data.withMicrowave &&
                  <li className={classNames("throughable", { "no": !data.withMicrowave })}>
                    <span className="label"><SpriteIcon hash="features.microwave" /></span>
                    <span>{intl.formatMessage(article.microwave)}</span>
                  </li>
                }
                {!!data.withRacletteSet &&
                  <li className={classNames("throughable", { "no": !data.withRacletteSet })}>
                    <span className="label"><SpriteIcon hash="features.raclette-set" /></span>
                    <span>{intl.formatMessage(article.raclette)}</span>
                  </li>
                }
                {!!data.withFondueSet &&
                  <li className={classNames("throughable", { "no": !data.withFondueSet })}>
                    <span className="label"><SpriteIcon hash="features.fondue-set" /></span>
                    <span>{intl.formatMessage(article.fondue)}</span>
                  </li>
                }
                {!!data.withHairDryer &&
                  <li className={classNames("throughable", { "no": !data.withHairDryer })}>
                    <span className="label"><SpriteIcon hash="features.hairdryer" /></span>
                    <span>{intl.formatMessage(article.hairdryer)}</span>
                  </li>
                }
                {!!data.isPetCompliant &&
                  <li className={classNames("throughable", { "no": !data.isPetCompliant })}>
                    <span className="label"><SpriteIcon hash="features.pet" /></span>
                    <span>{intl.formatMessage(article.pet)}</span>
                  </li>
                }
                {!data.isSmokerCompliant &&
                  <li className={classNames("throughable", { "no": !!data.isSmokerCompliant })}>
                    <span className="label"><SpriteIcon hash="features.no-smoker" /></span>
                    <span>{intl.formatMessage(article.smoker)}</span>
                  </li>
                }
                {!!data.isDisabledPersonCompliant &&
                  <li className={classNames("throughable", { "no": !data.isDisabledPersonCompliant })}>
                    <span className="label"><SpriteIcon hash="features.disabled-person" /></span>
                    <span>{intl.formatMessage(article.disabledPerson)}</span>
                  </li>
                }
                {!!data.withSmokeAlarm &&
                  <li className={classNames("throughable", { "no": !data.withSmokeAlarm })}>
                    <span className="label"><SpriteIcon hash="features.smoke-alarm" /></span>
                    <span>{intl.formatMessage(article.smokeAlarm)}</span>
                  </li>
                }
                {!!data.withCarbonAlarm &&
                  <li className={classNames("throughable", { "no": !data.withCarbonAlarm })}>
                    <span className="label"><SpriteIcon hash="features.any-alarm" /></span>
                    <span>{intl.formatMessage(article.carbonAlarm)}</span>
                  </li>
                }
                {!!data.withSportFacilities &&
                  <li className={classNames("throughable", { "no": !data.withSportFacilities })}>
                    <span className="label"><SpriteIcon hash="features.sport-facilities" /></span>
                    <span>{intl.formatMessage(article.sport)}</span>
                  </li>
                }
              </>
            }

            {!!data.withClearView &&
              <li className={classNames("throughable", { "no": !data.withClearView })}>
                <span className="label"><SpriteIcon hash="features.clear-view" /></span>
                <span>{intl.formatMessage(article.clearView)}</span>
              </li>
            }
            {!!data.nearSkiArea &&
              <li className={classNames("throughable", { "no": !data.nearSkiArea })}>
                <span className="label"><SpriteIcon hash="features.near-ski" /></span>
                <span>{intl.formatMessage(article.skiArea)}</span>
              </li>
            }
            {!!data.nearBusStop &&
              <li className={classNames("throughable", { "no": !data.nearBusStop })}>
                <span className="label"><SpriteIcon hash="features.near-bus" /></span>
                <span>{intl.formatMessage(article.busStop)}</span>
              </li>
            }

          </ul>
        </div>
      }
    </>);

};

export default FeaturesPart;
